/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Grid, Box } from '@tishman/components';
import { graphql, useStaticQuery } from 'gatsby';
import invariant from 'invariant';
import React, { Fragment, useMemo } from 'react';

import { PrintReceipt, PrintImage, PrintInstructions, PrintSummary } from '..';

import type {
  ITEM,
  OrderResponse,
  ORDERTICKET,
} from '../../../services/viva/types/response';

export type TicketPropsTicket = ORDERTICKET & {
  performanceTime?: string;
  dateOnly: boolean;
  item: ITEM;
};

interface TicketProps {
  offset?: number;
  order: OrderResponse;
  reservationAk?: string;
  ticket: TicketPropsTicket;
}

interface TicketImage {
  flow: string;
  src: {
    id: string;
    publicURL: string;
  };
}

const BUY_TICKETS_IMAGES_QUERY = graphql`
  query BuyTicketImages {
    dataJson(jsonId: { eq: "buy-tickets" }) {
      printTickets {
        ticketImages {
          flow
          src {
            id
            publicURL
          }
        }
      }
    }
  }
`;

export const SingleTicket = ({
  order,
  reservationAk,
  ticket,
}: TicketProps): React.JSX.Element => {
  const { dataJson } = useStaticQuery<Queries.BuyTicketImagesQuery>(
    BUY_TICKETS_IMAGES_QUERY,
  );

  invariant(dataJson, 'Buy Tickets print tickets JSON data is required!');

  const flow = useMemo(
    () => new URLSearchParams(location.search).get('Flow'),
    [location.search],
  );

  const defaultImage =
    'https://secure3.topoftherocknyc.com/TopOfTheRockWebAPI/api/TicketImageFile?TicketImageId=1';

  const images = dataJson.printTickets.ticketImages as TicketImage[];

  const matchingImage = images?.find(
    (image: TicketImage) => image.flow === flow,
  );

  const imageUrl = matchingImage?.src?.publicURL;

  return (
    <Fragment>
      <Grid
        sx={{
          'gridTemplateColumns': ['repeat(1, 1fr)', null, 'repeat(2, 1fr)'],
          'gap': 0,
          '@media print': {
            display: 'none',
          },
        }}
      >
        <PrintSummary order={order} ticket={ticket} />
        <PrintReceipt order={order} reservationAk={reservationAk} />
        <PrintImage src={imageUrl ?? defaultImage} />
        <PrintInstructions order={order} productAK={ticket.item.PRODUCT.AK} />
      </Grid>
      <Box
        sx={{
          'display': 'none',
          '@media print': {
            display: 'table',
            height: '100vh',
            overflow: 'hidden',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            td: {
              width: '50%',
            },
            pageBreakAfter: 'auto',
          },
        }}
      >
        <tr
          sx={{
            'height': '50vh',
            '@media print': {
              height: '45vh',
            },
          }}
        >
          <td
            style={{
              borderRight: 'dashed 2px #333',
            }}
          >
            <PrintSummary order={order} ticket={ticket} />
          </td>
          <td>
            <PrintReceipt order={order} reservationAk={reservationAk} />
          </td>
        </tr>
        <tr
          sx={{
            'height': '50vh',
            '@media print': {
              height: '55vh',
            },
          }}
        >
          <td
            style={{
              borderRight: 'dashed 2px #333',
              borderTop: 'dashed 2px #333',
            }}
          >
            {/* <PrintImage src={imageUrl ?? defaultImage} /> */}
          </td>
          <td
            style={{
              borderTop: 'dashed 2px #333',
            }}
          >
            <PrintInstructions
              order={order}
              productAK={ticket.item.PRODUCT.AK}
            />
          </td>
        </tr>
      </Box>
    </Fragment>
  );
};
